import React from 'react';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,

} from 'components/ui/sheet';
import { Button } from 'components/ui/button';

import { useTranslation } from 'react-i18next';
import { Bell, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';

import { parseISO } from 'date-fns';
import { NotificationItem, updateNotificationVisibility } from '../services/notification-service';
// import { NotificationType } from 'features/chat/types/notification-types';
import { getDateLabel } from 'utils/custom-date';
import { useNotificationFeedStore } from 'state/store/notifications/use-notification-feed-store';
import GroupNotification from './GroupNotification';
import { useConfigStore } from 'state/store/config/useConfigStore';
import { useToast } from 'hooks/use-toast';

interface NotificationSideSheetProps {
    isOpen: boolean;
    onClose: () => void;
}

export const NotificationSideSheet: React.FC<NotificationSideSheetProps> = ({
    isOpen,
    onClose,
}) => {

    const { t } = useTranslation();
    const { toast } = useToast();
    const [isMarkingAllAsRead, setIsMarkingAllAsRead] = React.useState(false);

    const {
        notificationList: notifications,
        isLoading,
        totalCount,
        currentPage,
        pageSize,
        goToPage,
        fetchAndSetNotification,
        getUnseenNotificationIds,
    } = useNotificationFeedStore();

    const configStore = useConfigStore();

    // console.log('notifications', notifications)


    // Sort notifications by createdDate (newest first) and group by date
    const sortedAndGroupedNotifications = React.useMemo(() => {
        if (!notifications?.length) return [];

        const sorted = [...notifications].sort((a, b) => {
            const dateA = a.createdDate
            const dateB = b.createdDate
            return new Date(dateB).getTime() - new Date(dateA).getTime();
        });

        const groups: { label: string; type?: string; verb?: string; notifications: NotificationItem[] }[] = [];
        let currentGroup: { label: string; type?: string; verb?: string; notifications: NotificationItem[] } | null = null;

        sorted.forEach((notification) => {
            const dateStr = notification.createdDate

            let date: Date;
            try {
                date = parseISO(dateStr);
            } catch {
                date = new Date(dateStr);
            }
            const groupLabel = getDateLabel(date, t);
            if (!currentGroup ||
                currentGroup.label !== groupLabel) {
                currentGroup = {
                    label: groupLabel,
                    notifications: []
                };
                groups.push(currentGroup);
            }

            currentGroup.notifications.push(notification);
        });

        return groups;
    }, [notifications]);

    // Calculate total pages
    const totalPages = React.useMemo(() => {
        if (totalCount === 0) return 0;
        return Math.ceil(totalCount / pageSize);
    }, [totalCount, pageSize]);

    // Generate page numbers to display (show max 5 pages around current)
    const pageNumbers = React.useMemo(() => {
        if (totalPages === 0) return [];

        const pages: number[] = [];
        const maxVisible = 5;
        let startPage = Math.max(0, currentPage - Math.floor(maxVisible / 2));
        const endPage = Math.min(totalPages - 1, startPage + maxVisible - 1);

        // Adjust start if we're near the end
        if (endPage - startPage < maxVisible - 1) {
            startPage = Math.max(0, endPage - maxVisible + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return pages;
    }, [currentPage, totalPages]);




    // console.log('sortedAndGroupedNotifications', sortedAndGroupedNotifications)

    const markCurrentPageAsSeen = async (targetPage: number) => {
        const unseenIds = getUnseenNotificationIds();
        if (!unseenIds.length) {
            return;
        }

        const config = configStore.configurationSettings;
        if (!config) {
            return;
        }

        const payload = {
            projectKey: config.blocksKey,
            itemIds: unseenIds,
            seen: true,
        };

        const response = await updateNotificationVisibility(config, payload);
        if (response.isSuccess) {
            await fetchAndSetNotification(targetPage, pageSize);
        }
    };

    const handleChangePage = async (targetPage: number) => {
        await goToPage(targetPage);
        await markCurrentPageAsSeen(targetPage);
    };

    const handleMarkAllAsRead = async () => {
        const unreadNotifications = notifications.filter((n) => !n.read);
        if (unreadNotifications.length === 0) {
            return;
        }

        setIsMarkingAllAsRead(true);
        try {
            const config = configStore.configurationSettings;
            if (!config) {
                throw new Error('Configuration settings not available');
            }

            const unreadIds = unreadNotifications.map((n) => n.itemId || n.id).filter((id): id is string => !!id);
            if (unreadIds.length === 0) {
                return;
            }

            const payload = {
                itemIds: [],
                projectKey: config.blocksKey,
                MarkAllRead: true,
            };

            const response = await updateNotificationVisibility(config, payload);
            if (response.isSuccess) {
                await fetchAndSetNotification(currentPage, pageSize);
                toast({
                    variant: 'success',
                    title: t('SUCCESS') || 'Success',
                    description: t('ALL_NOTIFICATIONS_MARKED_AS_READ') || 'All notifications marked as read',
                });
            } else {
                throw new Error('Failed to mark all notifications as read');
            }
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
            toast({
                variant: 'destructive',
                title: t('ERROR') || 'Error',
                description: t('FAILED_TO_MARK_ALL_AS_READ') || 'Failed to mark all notifications as read',
            });
        } finally {
            setIsMarkingAllAsRead(false);
        }
    };

    const hasUnreadNotifications = notifications.some((n) => !n.read);

    return (
        <Sheet open={isOpen} onOpenChange={onClose}>
            <SheetContent side="right" className="w-full sm:max-w-lg">
                <SheetHeader>
                    <SheetTitle>
                        {t('NOTIFICATIONS') || 'Notifications'}
                    </SheetTitle>
                </SheetHeader>

                {hasUnreadNotifications && (
                    <div className="mt-4 flex justify-end">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleMarkAllAsRead}
                            disabled={isMarkingAllAsRead || isLoading}
                            className="w-full sm:w-auto"
                        >
                            {isMarkingAllAsRead ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    {t('MARKING') || 'Marking...'}
                                </>
                            ) : (
                                t('MARK_ALL_AS_READ') || 'Mark all as read'
                            )}
                        </Button>
                    </div>
                )}

                <div className="mt-4">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 text-muted-foreground animate-spin mb-4" />
                            <p className="text-sm text-muted-foreground">
                                {t('LOADING_NOTIFICATIONS') || 'Loading notifications...'}
                            </p>
                        </div>
                    ) : notifications.length > 0 ? (
                        <div className="space-y-6">
                            <div className="max-h-[calc(100vh-200px)] overflow-y-auto space-y-6">
                                {sortedAndGroupedNotifications.map((group, groupIndex) => (
                                    <div key={`group-${groupIndex}-${group.label}`} className="space-y-3">
                                        <div className="sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-10 py-2">
                                            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                {group.label}
                                            </h3>
                                        </div>
                                        {group.notifications.map((notification) => {
                                            return <GroupNotification key={notification.itemId} notification={notification} onClose={onClose} />

                                        })}
                                    </div>
                                ))}
                            </div>
                            {totalPages > 1 && (
                                <div className="flex items-center justify-center gap-2 pt-4">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => handleChangePage(currentPage - 1)}
                                        disabled={isLoading || currentPage === 0}
                                        className="h-8 w-8"
                                        aria-label={t('PREVIOUS_PAGE') || 'Previous page'}
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>

                                    <div className="flex items-center gap-1">
                                        {pageNumbers.map((pageNum) => (
                                            <Button
                                                key={pageNum}
                                                variant={currentPage === pageNum ? "default" : "outline"}
                                                size="sm"
                                                onClick={() => handleChangePage(pageNum)}
                                                disabled={isLoading}
                                                className="h-8 min-w-8 px-2"
                                            >
                                                {pageNum + 1}
                                            </Button>
                                        ))}
                                    </div>

                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => handleChangePage(currentPage + 1)}
                                        disabled={isLoading || currentPage >= totalPages - 1}
                                        className="h-8 w-8"
                                        aria-label={t('NEXT_PAGE') || 'Next page'}
                                    >
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <Bell className="w-12 h-12 text-muted-foreground mb-4 opacity-50" />
                            <p className="text-sm font-medium text-foreground mb-1">
                                {t('NO_NOTIFICATIONS') || 'No notifications'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                {t('NO_NOTIFICATIONS_DESCRIPTION') ||
                                    "You're all caught up! New notifications will appear here."}
                            </p>
                        </div>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
};

